import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { HeroGlow } from "../components/ui/hero";
import { WavesLogo } from "../components/WavesLogo";
import { sanitizeRedirect } from "./Login";

// Landing page of an emailed login link (/login/link#<token>). Logging in
// takes a button press instead of happening on page load, so mail scanners
// that open every link in a message can't use up the one-time token.
export default function LoginLink() {
  const [token] = useState(() => window.location.hash.slice(1));
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(!token);
  const { refreshSession } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Clear the token from the address bar and browser history once read.
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  async function handleLogin() {
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/email-link/verify", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) { setFailed(true); return; }
      const { redirect } = await res.json() as { redirect: string };
      // Same reason as demo login: a session swap without a page reload
      // would otherwise render the previous account's cached queries.
      queryClient.clear();
      await refreshSession();
      navigate(sanitizeRedirect(redirect), { replace: true });
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center overflow-hidden bg-gradient-to-b from-white to-waves-subtle px-6 py-10 text-waves-ink md:px-16">
      <HeroGlow />

      <div className="relative z-[1] flex items-center gap-5 self-stretch">
        <WavesLogo className="h-[34px]" />
        <div className="h-[30px] w-px bg-waves-line" />
        <span className="text-[15px] font-black tracking-[-0.02em]">Partnerportaal</span>
      </div>

      <div className="relative z-[1] my-auto flex w-full max-w-[440px] flex-col gap-6 py-11">
        {failed ? (
          <>
            <h1 className="text-[36px] font-black leading-[1.06] tracking-[-0.025em]">Deze link werkt niet meer.</h1>
            <p className="text-[15px] leading-[1.65] text-waves-body">
              Een inloglink is 15 minuten geldig en werkt maar één keer. Vraag een nieuwe link aan op de inlogpagina.
            </p>
            <Link
              to="/login"
              className="flex items-center justify-center rounded-pill border border-waves-ink bg-white p-[15px] text-[15px] font-bold text-waves-ink transition-colors hover:bg-waves-mist"
            >
              Naar de inlogpagina
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-[36px] font-black leading-[1.06] tracking-[-0.025em]">
              Inloggen bij <span className="text-waves-accent">Waves Network</span>.
            </h1>
            <p className="text-[15px] leading-[1.65] text-waves-body">Klik op de knop om in te loggen op het Partnerportaal.</p>
            <button
              type="button"
              onClick={handleLogin}
              disabled={submitting}
              className="flex items-center justify-center rounded-pill bg-waves-ink p-[15px] text-[15px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? "Bezig met inloggen…" : "Inloggen"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
